import React, { useContext, useState, useEffect } from "react";
import {
  BlockStack,
  Box,
  Button,
  Icon,
  LegacyCard as Card,
  Layout,
  FormLayout,
  Modal,
  PageActions,
  Text,
  TextField,
} from "@shopify/polaris";
import { CheckCircleIcon, HideIcon, ViewIcon } from "@shopify/polaris-icons";

import { useQuery, useMutation } from "@apollo/client";

// import hoc
import { withErrorBoundary } from "lib/hoc";

import { PublicContext } from "lib/context";
import constant from "lib/constant/constant";
import { Banner, PasswordRequirements } from "lib/components";
import { baseHelper, storageHelper, errorHelper } from "lib/helpers";

import { GENERATE_PASSWORD } from "app/public/apollo/mutations";
import { GET_SELLER_DATA } from "app/public/apollo/queries";

import { useLocation, useNavigate } from "react-router-dom";
import validate from "./yup";

const GeneratePassword = () => {
  const { cms } = useContext(PublicContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [values, setValues] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [banner, setBanner] = useState({
    isOpen: false,
    status: "",
    title: "",
  });
  const [visible, setVisible] = useState({
    newPassword: false,
    confirmPassword: false,
  });
  const [errorMessage, setErrorMessage] = useState(true);
  const [isPasswordTouched, setIsPasswordTouched] = useState(false);
  const [isSetupSuccess, setIsSetupSuccess] = useState(false);
  const { CONFIRM_PASSWORD, CRITICAL, gql, NEW_PASSWORD, PASSWORD } = constant;

  const [sellerDetails, setSellerDetails] = useState();

  const queryParams = baseHelper.getQueryParams(location.search) || {};
  const { shop = "", token = "" } = queryParams;
  const { loading: sellerDataLoading, data: sellerData } = useQuery(GET_SELLER_DATA, {
    variables: { input: { shop } },
  });

  const [otp, { loading: otpLoading }] = useMutation(GENERATE_PASSWORD);

  useEffect(() => {
    if (sellerDataLoading) {
      return;
    }
    const responseData = baseHelper.getResponseData(sellerData, gql.GET_SELLER_DATA);
    if (responseData) {
      setSellerDetails({
        email: responseData.email.address,
        password: token,
      });
    }
  }, [sellerDataLoading, sellerData, gql.GET_SELLER_DATA, token]);

  const handleValidation = async (field, value) => {
    const validationError = await validate(field, value, cms);
    setErrorMessage((prevState) => ({
      ...prevState,
      [field]: validationError,
    }));
  };

  const handleChange = (key, value) => {
    setValues((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const toggleVisibility = (field) => {
    setVisible((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const renderViewIcon = (field) => (
    <Button
      id="eyeButton"
      variant="plain"
      icon={visible[field] ? HideIcon : ViewIcon}
      accessibilityLabel="Toggle password visibility"
      onClick={() => toggleVisibility(field)}
    />
  );

  const onSubmit = async () => {
    setIsPasswordTouched(true);
    await handleValidation(NEW_PASSWORD, values.newPassword);
    await handleValidation(CONFIRM_PASSWORD, {
      newPassword: values.newPassword,
      confirmPassword: values.confirmPassword,
    });
    const isAnyValidationError = Object.values(errorMessage).some((error) => error);
    const isAllValuesFilled = () => Object.values(values).every((value) => value);
    if (!isAnyValidationError && isAllValuesFilled()) {
      try {
        const reqData = {
          email:
            (sellerDetails && sellerDetails.email) ||
            (storageHelper.get("email") && storageHelper.get("email").toLowerCase()),
          password: (sellerDetails && sellerDetails.password) || storageHelper.get("password"),
          newPassword: values.newPassword,
        };

        const res = await otp({
          variables: { input: reqData },
        });
        const responseData = baseHelper.getResponseData(res.data, gql.GENERATE_PASSWORD);
        const responseError = baseHelper.getResponseError(res.data, gql.GENERATE_PASSWORD);

        if (!responseData) {
          setBanner({
            isOpen: true,
            status: CRITICAL,
            title: responseError || cms("message.error"),
          });
          return;
        }

        setBanner({ isOpen: false, status: "", title: "" });
        setIsSetupSuccess(true);
      } catch (exception) {
        setBanner({
          isOpen: true,
          status: CRITICAL,
          title: errorHelper.parse(exception),
        });
      }
    }
  };

  const displayEmail =
    (sellerDetails && sellerDetails.email) ||
    (storageHelper.get("email") && storageHelper.get("email").toLowerCase()) ||
    "";
  return (
    <>
      <Banner
        title={banner.title}
        status={banner.status}
        isOpen={banner.isOpen}
        onDismiss={() => setBanner({ isOpen: false, status: "", title: "" })}
      />
      <br />
      <Layout>
        <Layout.AnnotatedSection
          title="Welcome to MarketCube"
          description="Create a secure password to complete your account setup"
        >
          <Card sectioned>
            <FormLayout>
              <TextField
                id="email"
                label={`${cms("common.label.email")}*`}
                value={displayEmail}
                disabled
                readOnly
              />
              <TextField
                id="newPassword"
                label={`${cms("label.newPassword")}*`}
                type={visible.newPassword ? "text" : PASSWORD}
                value={values.newPassword || ""}
                placeholder={`${cms("placeholder.newPassword")}`}
                onChange={(value) => {
                  setIsPasswordTouched(true);
                  handleChange(NEW_PASSWORD, value);
                }}
                onBlur={() => [
                  setIsPasswordTouched(true),
                  handleValidation(NEW_PASSWORD, values.newPassword),
                  values.confirmPassword &&
                    handleValidation(CONFIRM_PASSWORD, {
                      newPassword: values.newPassword,
                      confirmPassword: values.confirmPassword,
                    }),
                ]}
                error={errorMessage && errorMessage.newPassword}
                suffix={renderViewIcon("newPassword")}
              />
              <TextField
                id="confirmPassword"
                label={`${cms("label.confirmNewPassword")}*`}
                placeholder={cms("placeholder.confirmNewPassword")}
                type={visible.confirmPassword ? "text" : PASSWORD}
                value={values.confirmPassword || ""}
                onChange={(value) => handleChange(CONFIRM_PASSWORD, value)}
                onBlur={() => {
                  handleValidation(CONFIRM_PASSWORD, {
                    newPassword: values.newPassword,
                    confirmPassword: values.confirmPassword,
                  });
                }}
                error={errorMessage && errorMessage.confirmPassword}
                suffix={renderViewIcon("confirmPassword")}
              />
              <PasswordRequirements password={values.newPassword} isTouched={isPasswordTouched} />
            </FormLayout>
          </Card>
          <PageActions
            primaryAction={{
              id: "resetPassword",
              content: cms("common.button.submit"),
              onAction: onSubmit,
              loading: otpLoading,
            }}
          />
        </Layout.AnnotatedSection>
      </Layout>
      <Modal
        open={isSetupSuccess}
        onClose={() => navigate("/login")}
        title="Password Created Successfully"
        primaryAction={{
          content: "Return to Login",
          onAction: () => navigate("/login"),
        }}
      >
        <Modal.Section>
          <BlockStack gap="400" inlineAlign="center">
            <Box paddingBlockStart="200" paddingBlockEnd="200">
              <Icon source={CheckCircleIcon} tone="success" />
            </Box>
            <Text as="p" alignment="center" tone="subdued">
              Your password has been set successfully. You can now log in to your MarketCube Dashboard using your new
              password.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
};

export default withErrorBoundary(GeneratePassword);
